/**
 * TriggerAppJobModal Component
 *
 * Confirmation modal for manually triggering a appJob execution.
 * Creates a background job for immediate execution.
 */

import React, { useState } from 'react';
import { BoltIcon } from '@heroicons/react/24/outline';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Alert } from '../../../components/ui/Alert';
import { useTriggerAppJob } from '../hooks/useTriggerAppJob';
import type { AppJobSanitized } from '../../../types/app-job.types';
import { describeCronExpression } from '../../../lib/validators/app-job';

interface TriggerAppJobModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  appJob: AppJobSanitized;
}

/**
 * TriggerAppJobModal component
 */
export const TriggerAppJobModal: React.FC<TriggerAppJobModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  appJob,
}) => {
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');

  const { mutate: triggerAppJob, isPending } = useTriggerAppJob(tenantId, {
    onSuccess: (result) => {
      setSuccessMessage(`Schedule triggered! Job ID: ${result.jobId}`);
      setErrorMessage('');
      // Auto-close after success
      setTimeout(() => {
        onClose();
        setSuccessMessage('');
      }, 2000);
    },
    onError: (error) => {
      setErrorMessage(error);
      setSuccessMessage('');
    },
  });

  const handleTrigger = () => {
    setErrorMessage('');
    setSuccessMessage('');
    triggerAppJob({
      appJobId: appJob.appJobId,
      applicationId: appJob.applicationId,
    });
  };

  const handleClose = () => {
    if (!isPending) {
      setErrorMessage('');
      setSuccessMessage('');
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <Modal.Header>Run App Job Now</Modal.Header>
      <Modal.Body>
        <div className="space-y-4">
          {/* Info Banner */}
          <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <BoltIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-blue-800">
              This will immediately create a background job to execute the appJobd function.
              The regular appJob will continue as normal.
            </p>
          </div>

          {/* Error Message */}
          {errorMessage && <Alert variant="error">{errorMessage}</Alert>}

          {/* Success Message */}
          {successMessage && <Alert variant="success">{successMessage}</Alert>}

          {/* App Job Details */}
          <div className="bg-gray-50 p-4 rounded-lg space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Schedule Name</span>
              <span className="text-sm font-medium text-gray-900">{appJob.name}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Application</span>
              <span className="text-sm font-medium text-gray-900">{appJob.applicationCode}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Function</span>
              <code className="text-sm bg-white px-2 py-0.5 rounded border">
                {appJob.functionName}
              </code>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Normal Schedule</span>
              <span className="text-sm text-gray-700">
                {describeCronExpression(appJob.cronExpression)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Timezone</span>
              <span className="text-sm text-gray-700">{appJob.timezone}</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={handleClose} disabled={isPending}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              loading={isPending}
              onClick={handleTrigger}
              disabled={!!successMessage}
            >
              <BoltIcon className="h-4 w-4 mr-1" />
              Run Now
            </Button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};
